'use client';

import { useMemo } from 'react';
import type { PlatformAlert, PlatformOverview, TenantRow } from '../types';

function fmtMoney(n: number) {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

function fmtNum(n: number) {
  return new Intl.NumberFormat('pt-PT').format(n || 0);
}

export function OverviewPanel({
  overview,
  tenants,
  alerts,
  loading,
}: {
  overview: PlatformOverview | null;
  tenants: TenantRow[];
  alerts: PlatformAlert[];
  loading: boolean;
}) {
  const topTenants = useMemo(
    () => [...tenants].sort((a, b) => b.orders_7d - a.orders_7d).slice(0, 5),
    [tenants],
  );

  if (loading) {
    return (
      <div className="sa-panel-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="sa-kpi sa-kpi-skeleton" />
        ))}
      </div>
    );
  }

  const kpis = [
    { label: 'Tenants ativos', value: fmtNum(overview?.tenants_active ?? 0), sub: `${overview?.tenants_total ?? 0} total` },
    { label: 'Utilizadores tenant', value: fmtNum(overview?.tenant_users ?? 0), sub: 'contas ativas' },
    { label: 'Admins plataforma', value: fmtNum(overview?.platform_admins ?? 0), sub: 'super-admin' },
    { label: 'Dominios ativos', value: fmtNum(overview?.domains_active ?? 0), sub: 'hosts' },
    { label: 'Numeros de voz', value: fmtNum(overview?.voice_numbers_active ?? 0), sub: 'DIDs ativos' },
    { label: 'Pedidos 7 dias', value: fmtNum(overview?.orders_7d ?? 0), sub: `${fmtNum(overview?.orders_24h ?? 0)} nas ultimas 24h` },
  ];

  return (
    <div className="space-y-6">
      {alerts.length > 0 && (
        <section className="space-y-2">
          {alerts.slice(0, 6).map((a) => (
            <a
              key={a.id}
              href={a.href ?? '#'}
              className={`sa-alert sa-alert-${a.severity}`}
            >
              <div>
                <p className="font-medium text-sm">{a.title}</p>
                <p className="text-xs opacity-80">{a.detail}</p>
              </div>
            </a>
          ))}
        </section>
      )}
      <div className="sa-panel-grid">
        {kpis.map((k) => (
          <div key={k.label} className="sa-kpi">
            <p className="sa-kpi-label">{k.label}</p>
            <p className="sa-kpi-value">{k.value}</p>
            <p className="sa-kpi-sub">{k.sub}</p>
          </div>
        ))}
      </div>

      <div className="sa-two-col">
        <section className="sa-card">
          <header className="sa-card-header">
            <h3 className="sa-card-title">Receita (7 dias)</h3>
            <p className="sa-card-desc">Total de pedidos nao cancelados</p>
          </header>
          <p className="sa-stat-hero">{fmtMoney(overview?.revenue_7d ?? 0)}</p>
        </section>

        <section className="sa-card">
          <header className="sa-card-header">
            <h3 className="sa-card-title">Top tenants (7 dias)</h3>
            <p className="sa-card-desc">Por volume de pedidos</p>
          </header>
          <div className="sa-mini-table">
            {topTenants.length === 0 ? (
              <p className="text-sm text-ink-500 px-1">Sem dados de pedidos.</p>
            ) : (
              topTenants.map((t) => (
                <div key={t.id} className="sa-mini-row">
                  <div className="min-w-0">
                    <p className="font-medium text-ink-900 truncate">{t.name}</p>
                    <p className="text-xs text-ink-500 font-mono">{t.slug}</p>
                  </div>
                  <span className="sa-pill">{t.orders_7d} pedidos</span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <section className="sa-card">
        <header className="sa-card-header sa-card-header-row">
          <div>
            <h3 className="sa-card-title">Todos os tenants</h3>
            <p className="sa-card-desc">Estado rapido da plataforma</p>
          </div>
        </header>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Restaurante</th>
                <th>Estado</th>
                <th>Dominios</th>
                <th>Users</th>
                <th>Voz</th>
                <th>Pedidos</th>
                <th>Landing</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div className="font-medium text-ink-900">{t.name}</div>
                    <div className="text-xs text-ink-500 font-mono">{t.slug}</div>
                  </td>
                  <td>
                    <span className={`sa-status sa-status-${t.status === 'active' ? 'ok' : 'muted'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td>{t.domains.length}</td>
                  <td>{t.users.filter((u) => u.is_active).length}</td>
                  <td>{t.voice_numbers.filter((v) => v.is_active).length}</td>
                  <td>{fmtNum(t.order_count)}</td>
                  <td>{t.landing_visible ? 'Visivel' : 'Oculto'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
